import { type SupplierListItem } from '@choco-admin/types';
import { Button, Chip } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiUsers, FiMessageSquare, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { BaseButton } from '../../../../../components/BaseButton';

interface SupplierHeaderProps {
  supplier: SupplierListItem;
}

export const SupplierHeader: React.FC<SupplierHeaderProps> = ({ supplier }) => {
  const navigate = useNavigate();

  const handleShowUsers = useCallback(() => {
    if (supplier.id) {
      navigate({
        pathname: '/users',
        search: `?search=${encodeURIComponent(supplier.id)}`,
      });
    }
  }, [navigate, supplier.id]);

  const handleShowChats = useCallback(() => {
    if (supplier.botUserId) {
      navigate({
        pathname: '/chats',
        search: `?search=${encodeURIComponent(
          supplier.botUserId
        )}&type=all&enabledState=enabled`,
      });
    }
  }, [navigate, supplier.botUserId]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-4">
        <BaseButton
          variant="light"
          startContent={<FiArrowLeft size={16} />}
          onPress={handleBack}
        >
          Back
        </BaseButton>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-foreground">
            {supplier.name || 'Unnamed Supplier'}
          </h1>
          <div className="flex items-center gap-2">
            <Chip
              variant="bordered"
              color="default"
              size="sm"
              classNames={{
                base: 'bg-default-100 text-default-700',
                content: 'font-mono text-xs',
              }}
            >
              {supplier.referenceId || 'N/A'}
            </Chip>
            <Chip color="primary" variant="flat" size="sm">
              {supplier.accessPackage || 'free'}
            </Chip>
            {supplier.isIntegrated && (
              <Chip color="success" variant="flat" size="sm">
                Integrated
              </Chip>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="bordered"
          startContent={<FiUsers size={16} />}
          onPress={handleShowUsers}
          isDisabled={!supplier.id}
        >
          Show Users
        </Button>
        <Button
          variant="bordered"
          startContent={<FiMessageSquare size={16} />}
          onPress={handleShowChats}
          isDisabled={!supplier.botUserId}
        >
          Show Chats
        </Button>
      </div>
    </div>
  );
};
